import React, { useEffect, useRef, useState } from 'react'

import { Grid, NoticeBox, VsExclamationIcon } from '@octane/spark'
import postNADAAccessories, {
    NADAAccessory,
} from 'api/vehicles/postNADAAccessories'
import postNadaValuation, {
    NadaValuationRequest,
    NadaValuationResponse,
} from 'api/vehicles/postNadaValuation'
import { AxiosError } from 'axios'
import { FormikAmountField, FormikTextInput } from 'components/FormikFields'
import { FieldKit } from 'containers/FormBuilder'
import { FormikValues, getIn } from 'formik'
import AssetType from 'state/enums/AssetType'

import FormContent from '../../components/FormContent'
import FormGridPlacement from '../../components/FormGridPlacement'
import { FormikCheckbox } from '../../components/FormikFields/FormikCheckbox/FormikCheckbox.styled'
import { OctaneLogger } from '../../integrations/datadog/OctaneLogger'
import {
    BookingSource,
    isLastYearOrNewer,
    isLikeInvoice,
} from '../../utils/bookingSourceHelper'
import ConditionTab from '../VehicleSelection/ConditionTab'
import BookingSourceDropdown from '../VehicleSelection/dropdowns/BookingSourceDropdown'
import {
    AccessoriesSection,
    AccessoryCategoryTitle,
    AccessoryItem,
    AccessoriesTitle,
    BorderLine,
    CpoCheckboxContainer,
    CpoCheckboxLabel,
    TooltipListContainer,
    VinSectionContainer,
} from './AutomobileVehicleSelection.styled'
import {
    MakeDropdown,
    ModelDropdown,
    TrimDropdown,
    YearDropdown,
} from './dropdowns'

interface AutomobileVehicleSelectionProps {
    assetType: AssetType
    formik: FieldKit<FormikValues>
    prefilled?: boolean
}

const defaultProps = {
    prefilled: false,
}

const DATA_SOURCE = ['LOS_ADMIN', 'LOS', 'NADA']

const LendingValueTooltipText = () => (
    <span>
        This amount is calculated based on the vehicle&apos;s mileage:
        <br />
        <TooltipListContainer>
            <li>{'<='} 6,000 miles = 90% like invoice</li>
            <li>6,001 - 35,000 miles = 80% like invoice</li>
            <li>35,001 - 75,000 miles = 70% like invoice</li>
            <li>75,001+ miles = 60% like invoice</li>
        </TooltipListContainer>
    </span>
)

export default function AutomobileVehicleSelection({
    assetType,
    formik,
    prefilled,
}: AutomobileVehicleSelectionProps) {
    const [modelAsTextInput, setModelAsTextInput] = useState(false)
    const [trimAsTextInput, setTrimAsTextInput] = useState(false)
    const { values } = formik
    const { vehicle } = values
    const { condition, year, bookingSource, mileage, vin } = vehicle

    const parentApplicationId =
        typeof values.id === 'number' && values.id > 0 ? values.id : undefined

    const [nadaError, setNadaError] = useState(false)
    const [nadaNotFound, setNadaNotFound] = useState(false)
    const [nadaTrimState, setNadaTrimState] = useState(false)

    const [nadaAccessories, setNADAAccessories] = useState<
        NADAAccessory[] | null
    >(null)

    const selectedOptions: NADAAccessory[] = vehicle.nadaAccessories ?? []
    const selectedOptionCodes: string[] = selectedOptions.map((a) => a.acccode)

    const [vinAvailableIds, setVinAvailableIds] = useState<
        number[] | undefined
    >(undefined)

    const [nadaResolved, setNadaResolved] = useState(false)
    const [nadaNoValuation, setNadaNoValuation] = useState(false)
    const [resolvedFromVin, setResolvedFromVin] = useState(false)
    const prevVinRef = useRef<string>(vin ?? '')
    const prevAutomobileIdRef = useRef<string>(vehicle.automobileId ?? '')
    const prevConditionRef = useRef<string>(condition ?? '')
    // Tracks whether the initial collateral load has been processed (copy-into-new)
    const initialLoadProcessedRef = useRef<boolean>(!!vehicle.automobileId)
    const prevMileageRef = useRef<number | undefined>(undefined)
    const nadaRequestIdRef = useRef(0)
    const mileageRequestIdRef = useRef(0)
    // Stores nada_id so the mileage effect can fetch accessories on first entry
    const nadaIdRef = useRef<string | null>(null)

    const isUsedAutomobile =
        assetType === AssetType.AUTOMOBILE && condition === 'used'

    let lendingValueFactor = 1.0 // 100% of the value
    const bSLikeInvoiceCondition = isLikeInvoice(year, bookingSource)

    if (bSLikeInvoiceCondition) {
        if (mileage && vehicle.likeInvoice) {
            if (Number.isNaN(mileage) || mileage < 0) {
                vehicle.lendingValue = ''
            } else {
                // Rules:
                // <= 6,000 --> 90%
                // > 6,000 && <= 35,000 --> 80%
                // > 35,000 && <= 75,000 --> 70%
                // > 75,000 --> 60%
                if (mileage <= 6000) lendingValueFactor = 0.9
                else if (mileage <= 35000) lendingValueFactor = 0.8
                else if (mileage <= 75000) lendingValueFactor = 0.7
                else lendingValueFactor = 0.6

                vehicle.lendingValue = Math.ceil(
                    Number(vehicle.likeInvoice) * lendingValueFactor,
                )
            }
        } else {
            vehicle.lendingValue = ''
        }
    }

    function resetAccessories() {
        setNADAAccessories(null)
        formik.setFieldValue('vehicle.nadaAccessories', [])
    }

    function applyNadaResolved(data: {
        automobile_id: number
        year: number
        make_id: number
        model_id: number
        value?: string
        nada_id?: string
    }) {
        formik.setFieldValue('vehicle.year', data.year.toString())
        formik.setFieldValue('vehicle.makeId', data.make_id.toString())
        formik.setFieldValue('vehicle.modelId', data.model_id.toString())

        formik.setFieldValue(
            'vehicle.automobileId',
            data.automobile_id.toString(),
        )

        setNadaResolved(true)
        nadaIdRef.current = data.nada_id || ''

        if (condition === 'used') {
            if (data.value) {
                formik.setFieldValue(
                    'vehicle.bookingSource',
                    BookingSource.NADA,
                )

                formik.setFieldValue('vehicle.wholesale', data.value)
            }

            postNADAAccessories({
                nada_id: data.nada_id,
                automobile_id: data.automobile_id,
            })
                .then(setNADAAccessories)
                .catch(() => {})
        }
    }

    function applyMileageResponse(response: NadaValuationResponse) {
        if (response.resolved && 'value' in response && response.value) {
            formik.setFieldValue('vehicle.wholesale', response.value)
            formik.setFieldValue('vehicle.bookingSource', BookingSource.NADA)
        } else {
            // NADA couldn't provide a value for this mileage — fall back to like invoice.
            // Only set LIKE_INVOICE booking source for recent-year vehicles; for older vehicles
            // LIKE_INVOICE is not a valid booking source option so leave it empty for the user to pick.
            setNadaNoValuation(true)

            if (isLastYearOrNewer(Number(year))) {
                formik.setFieldValue(
                    'vehicle.bookingSource',
                    BookingSource.LIKE_INVOICE,
                )
            }
        }
    }

    async function fireNadaLookup(request: NadaValuationRequest) {
        const requestId = ++nadaRequestIdRef.current

        ++mileageRequestIdRef.current

        // Capture before clearing so the copy-into-new flow can preselect the original trim
        const existingAutomobileId = vehicle.automobileId
            ? Number(vehicle.automobileId)
            : undefined

        if (request.vin) {
            formik.setFieldValue('vehicle.year', '')
            formik.setFieldValue('vehicle.makeId', '')
            formik.setFieldValue('vehicle.make', '')
            formik.setFieldValue('vehicle.modelId', '')
            formik.setFieldValue('vehicle.model', '')
            formik.setFieldValue('vehicle.trim', '')
            formik.setFieldValue('vehicle.automobileId', '')
        }

        setNadaError(false)
        setNadaNotFound(false)
        setNadaTrimState(false)
        setVinAvailableIds(undefined)

        setNadaResolved(false)
        setNadaNoValuation(false)
        setResolvedFromVin(false)
        resetAccessories()

        try {
            const response = await postNadaValuation(request)

            if (requestId !== nadaRequestIdRef.current) return

            if (response.resolved) {
                // Single vehicle identified — use NADA value if present, else like invoice
                setResolvedFromVin(!!request.vin)
                setVinAvailableIds([response.automobile_id])
                applyNadaResolved(response)

                if (!('value' in response) || !response.value) {
                    if (request.mileage) {
                        setNadaNoValuation(true)

                        if (isLastYearOrNewer(response.year)) {
                            formik.setFieldValue(
                                'vehicle.bookingSource',
                                BookingSource.LIKE_INVOICE,
                            )
                        }
                    }
                }
            } else if (
                'available_automobile_ids' in response &&
                'year' in response &&
                response.available_automobile_ids.length
            ) {
                // Multiple trims — pre-fill identity fields
                setResolvedFromVin(!!request.vin)
                formik.setFieldValue('vehicle.bookingSource', '')
                formik.setFieldValue('vehicle.wholesale', '')
                formik.setFieldValue('vehicle.year', response.year.toString())

                formik.setFieldValue(
                    'vehicle.makeId',
                    response.make_id.toString(),
                )

                formik.setFieldValue(
                    'vehicle.modelId',
                    response.model_id.toString(),
                )

                setVinAvailableIds(response.available_automobile_ids)

                const preselectedId =
                    existingAutomobileId &&
                    response.available_automobile_ids.includes(
                        existingAutomobileId,
                    )
                        ? existingAutomobileId
                        : undefined

                if (preselectedId) {
                    // Copy-into-new: reuse the original trim, skip the picker notice
                    formik.setFieldValue(
                        'vehicle.automobileId',
                        preselectedId.toString(),
                    )

                    if (mileage && !getIn(formik.errors, 'vehicle.mileage')) {
                        prevMileageRef.current = Number(mileage)

                        const mileageRequestId = ++mileageRequestIdRef.current

                        postNadaValuation({
                            automobile_id: preselectedId,
                            mileage: Number(mileage),
                            ...(selectedOptionCodes.length
                                ? { option_codes: selectedOptionCodes }
                                : {}),
                            ...(parentApplicationId
                                ? {
                                      parent_application_id:
                                          parentApplicationId,
                                  }
                                : {}),
                        })
                            .then((mileageResponse) => {
                                if (
                                    mileageRequestId !==
                                    mileageRequestIdRef.current
                                )
                                    return

                                applyMileageResponse(mileageResponse)
                            })
                            .catch((err: Error) => {
                                if (
                                    mileageRequestId !==
                                    mileageRequestIdRef.current
                                )
                                    return

                                OctaneLogger.error(
                                    'Failed to update wholesale on trim preselection',
                                    {},
                                    err,
                                )
                            })
                    }
                } else {
                    // Fresh form: show the trim picker
                    setNadaTrimState(true)
                }
            } else {
                setNadaNotFound(true)
            }
        } catch (err) {
            if (requestId !== nadaRequestIdRef.current) return

            const status = (err as AxiosError)?.response?.status

            if (status === 400) {
                OctaneLogger.error('NADA VIN lookup failed', {}, err as Error)
                setNadaError(true)
            } else {
                setNadaNoValuation(true)
            }
        }
    }

    function buildVinRequest(): NadaValuationRequest {
        const conditionCode: 'N' | 'U' = condition === 'new' ? 'N' : 'U'

        return {
            vin,
            condition: conditionCode,
            ...(condition === 'used' && mileage
                ? { mileage: Number(mileage) }
                : {}),
            ...(parentApplicationId
                ? { parent_application_id: parentApplicationId }
                : {}),
        }
    }

    // Clear accessories when vehicle identity is lost (year/make/model change clears automobileId).
    // Only fetch accessories on initial load (prevAutomobileId empty = copy-into-new reinitialization)
    // — all other automobileId changes are driven by applyNadaResolved or handleAutomobileSelected,
    // which already call postNADAAccessories themselves.
    useEffect(() => {
        if (prefilled) return

        const prevAutomobileId = prevAutomobileIdRef.current

        prevAutomobileIdRef.current = vehicle.automobileId ?? ''

        if (!vehicle.automobileId) {
            // Only clear if we previously had a vehicle selected (not on initial mount)
            if (prevAutomobileId) {
                resetAccessories()
            }
        } else if (!prevAutomobileId && condition === 'used') {
            // Initial load only (copy-into-new reinitialization): seed the display list
            // from preselected accessories immediately, then fetch the full list.
            if (nadaAccessories === null && vehicle.nadaAccessories?.length) {
                setNADAAccessories(vehicle.nadaAccessories)
            }

            postNADAAccessories({
                nada_id: nadaIdRef.current,
                automobile_id: Number(vehicle.automobileId),
            })
                .then(setNADAAccessories)
                .catch(() => {})

            // Fire a valuation call with the preselected accessories so the backend can honor
            // the parent app's value. Covers both VIN and no-VIN copy-into-new.
            if (mileage && parentApplicationId) {
                const initRequestId = ++mileageRequestIdRef.current

                postNadaValuation({
                    automobile_id: Number(vehicle.automobileId),
                    mileage: Number(mileage),
                    ...(selectedOptionCodes.length
                        ? { option_codes: selectedOptionCodes }
                        : {}),
                    parent_application_id: parentApplicationId,
                })
                    .then((response) => {
                        if (initRequestId !== mileageRequestIdRef.current)
                            return

                        applyMileageResponse(response)
                    })
                    .catch(() => {})
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vehicle.automobileId])

    // Fire NADA lookup when VIN reaches 17 characters
    useEffect(() => {
        const prevVin = prevVinRef.current

        prevVinRef.current = vin ?? ''

        if (prefilled || vin === prevVin) return

        if (vin?.length === 17 && condition) {
            // Skip lookup if the vehicle is already fully identified and this is the initial
            // load transition (reinitialization from copy-into-new collateral data).
            // Only skip when prevVin was empty — meaning this is the first time the field
            // is populated from existing data, not a user edit.
            if (
                vehicle.automobileId &&
                !initialLoadProcessedRef.current &&
                !prevVin
            ) {
                initialLoadProcessedRef.current = true
                setResolvedFromVin(true)
                setNadaResolved(true)

                return
            }

            initialLoadProcessedRef.current = true

            // New complete VIN — include any existing mileage so valuation resolves in one request
            fireNadaLookup(buildVinRequest())
        } else {
            // VIN cleared/shortened — unlock dropdowns and clear identification state,
            // but preserve nadaNoValuation and booking state so like-invoice persists
            // until the user actually changes the vehicle.
            setNadaError(false)
            setNadaNotFound(false)
            setNadaTrimState(false)
            setVinAvailableIds(undefined)
            setResolvedFromVin(false)
            setNadaResolved(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vin])

    // Re-fire VIN lookup when condition changes with a VIN already entered
    useEffect(() => {
        const prevCondition = prevConditionRef.current

        prevConditionRef.current = condition ?? ''

        if (prefilled) return

        // Skip if condition hasn't actually changed (initial mount or same value)
        if (!prevCondition || condition === prevCondition) return

        setNadaError(false)
        setNadaNotFound(false)
        setNadaTrimState(false)
        setVinAvailableIds(undefined)
        setNadaResolved(false)
        setNadaNoValuation(false)
        prevMileageRef.current = undefined

        if (vin?.length === 17) {
            resetAccessories()
            fireNadaLookup(buildVinRequest())
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [condition])

    function handleMileageBlur(newMileage: number | undefined) {
        if (!newMileage || !vehicle.automobileId) return

        if (getIn(formik.errors, 'vehicle.mileage')) return

        if (newMileage === prevMileageRef.current) return

        prevMileageRef.current = newMileage

        // Reset no-valuation state before firing — a new mileage may yield a value
        setNadaNoValuation(false)

        const mileageRequestId = ++mileageRequestIdRef.current

        postNadaValuation({
            automobile_id: Number(vehicle.automobileId),
            mileage: newMileage,
            ...(selectedOptionCodes.length
                ? { option_codes: selectedOptionCodes }
                : {}),
            ...(parentApplicationId
                ? { parent_application_id: parentApplicationId }
                : {}),
        })
            .then((response) => {
                if (mileageRequestId !== mileageRequestIdRef.current) return

                applyMileageResponse(response)
            })
            .catch((err: Error) => {
                if (mileageRequestId !== mileageRequestIdRef.current) return

                OctaneLogger.error(
                    'Failed to update wholesale on mileage change',
                    {},
                    err,
                )
            })
    }

    function handleAutomobileSelected(automobileId: number) {
        if (nadaTrimState || vinAvailableIds?.includes(automobileId)) {
            // Trim was selected from the VIN trim picker — user made the selection, so nadaResolved stays false.
            // resolvedFromVin stays true so year/make/model remain locked.
            setNadaTrimState(false)

            // Fetch accessories immediately — they don't depend on mileage
            if (condition === 'used') {
                postNADAAccessories({
                    nada_id: nadaIdRef.current,
                    automobile_id: automobileId,
                })
                    .then(setNADAAccessories)
                    .catch(() => {})
            }

            if (mileage && !getIn(formik.errors, 'vehicle.mileage')) {
                prevMileageRef.current = Number(mileage)
                const mileageRequestId = ++mileageRequestIdRef.current

                postNadaValuation({
                    automobile_id: automobileId,
                    mileage: Number(mileage),
                    ...(selectedOptionCodes.length
                        ? { option_codes: selectedOptionCodes }
                        : {}),
                    ...(parentApplicationId
                        ? { parent_application_id: parentApplicationId }
                        : {}),
                })
                    .then((response) => {
                        if (mileageRequestId !== mileageRequestIdRef.current)
                            return

                        applyMileageResponse(response)
                    })
                    .catch((err: Error) => {
                        if (mileageRequestId !== mileageRequestIdRef.current)
                            return

                        OctaneLogger.error(
                            'Failed to update wholesale on trim picker selection',
                            {},
                            err,
                        )
                    })
            }

            return
        }

        // Manual trim change — reset valuation state so mileage blur fetches fresh valuation
        // Only reset if the trim actually changed (not a reinitialization auto-select of the same trim)
        const trimChanged = vehicle.automobileId !== automobileId.toString()

        prevMileageRef.current = undefined
        setResolvedFromVin(false)

        setNadaResolved(false)
        setNadaNoValuation(false)

        if (trimChanged) {
            formik.setFieldValue('vehicle.mileage', '')
            formik.setFieldValue('vehicle.wholesale', '')
            formik.setFieldValue('vehicle.bookingSource', '')
            resetAccessories()
        }

        if (condition === 'used') {
            postNADAAccessories({
                nada_id: nadaIdRef.current,
                automobile_id: automobileId,
            })
                .then(setNADAAccessories)
                .catch(() => {})
        }
    }

    // fromVinFull locks trim only when NADA auto-resolved it — false when user picks from picker or selects manually
    const fromVinFull = nadaResolved && !nadaTrimState

    function handleAccessoryToggle(accessory: NADAAccessory, checked: boolean) {
        const all = nadaAccessories ?? []

        // Codes that are auto-bundled as children of currently selected parents
        // (excluding the accessory being toggled, since we're recomputing)
        const otherSelected = selectedOptions.filter(
            (a) => a.acccode !== accessory.acccode,
        )

        const childCodesFromOthers = new Set(
            otherSelected.flatMap((a) => a.includes).filter(Boolean),
        )

        let newSelected: NADAAccessory[]

        if (checked) {
            // Add the accessory itself
            const toAdd = [accessory]

            // Auto-add any children from its includes list
            if (accessory.includes?.length) {
                accessory.includes.forEach((childCode) => {
                    const child = all.find((a) => a.acccode === childCode)

                    if (
                        child &&
                        !selectedOptions.find((a) => a.acccode === childCode)
                    ) {
                        toAdd.push(child)
                    }
                })
            }

            // Remove anything excluded by this accessory
            const excludedCodes = new Set(accessory.excludes ?? [])

            newSelected = [
                ...otherSelected.filter((a) => !excludedCodes.has(a.acccode)),
                ...toAdd,
            ]
        } else {
            // Remove the accessory and any children it bundled
            // (only remove children that aren't also bundled by another selected parent)
            const childrenBundledByThis = new Set(accessory.includes ?? [])

            newSelected = otherSelected.filter(
                (a) =>
                    !childrenBundledByThis.has(a.acccode) ||
                    childCodesFromOthers.has(a.acccode),
            )
        }

        formik.setFieldValue('vehicle.nadaAccessories', newSelected)

        // Re-fire mileage valuation with new option_codes so the backend returns
        // the correct value for this accessory set (avoids double-counting).
        // Skip if there's no NADA valuation — accessories have no value to add to.
        if (
            condition === 'used' &&
            vehicle.automobileId &&
            mileage &&
            !nadaNoValuation &&
            !getIn(formik.errors, 'vehicle.mileage')
        ) {
            const newOptionCodes = newSelected.map((a) => a.acccode)
            const accessoryRequestId = ++mileageRequestIdRef.current

            postNadaValuation({
                automobile_id: Number(vehicle.automobileId),
                mileage: Number(mileage),
                ...(newOptionCodes.length
                    ? { option_codes: newOptionCodes }
                    : {}),
                ...(parentApplicationId
                    ? { parent_application_id: parentApplicationId }
                    : {}),
            })
                .then((response) => {
                    if (accessoryRequestId !== mileageRequestIdRef.current)
                        return

                    applyMileageResponse(response)
                })
                .catch(() => {})
        }
    }

    // Codes that are auto-bundled as children of a selected parent
    const bundledChildCodes = new Set(
        selectedOptions.flatMap((a) => a.includes ?? []),
    )

    // Codes that are excluded because a selected accessory excludes them
    const excludedCodes = new Set(
        selectedOptions.flatMap((a) => a.excludes ?? []),
    )

    const groupAccessoriesByCategory = (accessories: NADAAccessory[]) => {
        const grouped = accessories.reduce(
            (acc, accessory) => {
                const category = accessory.accessorycategory || 'Other'

                if (!acc[category]) {
                    acc[category] = []
                }

                acc[category].push(accessory)

                return acc
            },
            {} as Record<string, NADAAccessory[]>,
        )

        return Object.entries(grouped).sort(([catA], [catB]) =>
            catA.localeCompare(catB),
        )
    }

    const allAccessoriesGrouped =
        nadaAccessories && nadaAccessories.length > 0
            ? groupAccessoriesByCategory(nadaAccessories)
            : []

    return (
        <>
            <FormContent gridTemplate="1fr">
                <ConditionTab prefilled={prefilled} />
            </FormContent>
            <BorderLine />
            <FormContent gridTemplate="1fr 1fr">
                <VinSectionContainer>
                    <FormikTextInput
                        name="vehicle.vin"
                        label="VIN (optional)"
                        disabled={prefilled}
                        prefilled={prefilled}
                        maxLength={17}
                    />
                    {nadaError && (
                        <NoticeBox
                            title="Vehicle not covered"
                            icon={VsExclamationIcon}
                            color="warning"
                            variant="box-highlighted"
                        >
                            Check the condition, year, make, or vehicle class
                            (commercial vehicles not supported).
                        </NoticeBox>
                    )}
                    {nadaNotFound && (
                        <NoticeBox
                            title="VIN not found"
                            icon={VsExclamationIcon}
                            color="warning"
                            variant="box-highlighted"
                        >
                            Check for a typo or enter details manually below.
                        </NoticeBox>
                    )}
                    {year && Number(year) < new Date().getFullYear() - 11 && (
                        <NoticeBox
                            title="Older model year selected"
                            icon={VsExclamationIcon}
                            color="note"
                            variant="box-highlighted"
                        >
                            Please review your program guide to confirm
                            coverage.
                        </NoticeBox>
                    )}
                    {nadaTrimState && !nadaResolved && (
                        <NoticeBox
                            title="Multiple trims found for this VIN"
                            icon={VsExclamationIcon}
                            color="note"
                            variant="box-highlighted"
                        >
                            Please select the correct trim below to continue.
                        </NoticeBox>
                    )}
                </VinSectionContainer>
                <FormGridPlacement gridArea=" 2 / 1 ">
                    <YearDropdown
                        formik={formik}
                        dataSource={DATA_SOURCE}
                        assetType={assetType}
                        prefilled={prefilled || resolvedFromVin}
                    />
                    <MakeDropdown
                        formik={formik}
                        dataSource={DATA_SOURCE}
                        assetType={assetType}
                        prefilled={prefilled || resolvedFromVin}
                    />
                    {prefilled ? (
                        <>
                            <FormikTextInput
                                name="vehicle.model"
                                label="Model"
                                disabled
                                prefilled
                            />
                            <FormikTextInput
                                name="vehicle.trim"
                                label="Trim"
                                disabled
                                prefilled={prefilled}
                            />
                        </>
                    ) : (
                        <>
                            <ModelDropdown
                                formik={formik}
                                dataSource={DATA_SOURCE}
                                assetType={assetType}
                                prefilled={prefilled || resolvedFromVin}
                                onModelChanged={() => {
                                    prevMileageRef.current = undefined
                                    setResolvedFromVin(false)
                                    setNadaResolved(false)
                                    setNadaNoValuation(false)
                                    formik.setFieldValue('vehicle.mileage', '')

                                    formik.setFieldValue(
                                        'vehicle.wholesale',
                                        '',
                                    )

                                    formik.setFieldValue(
                                        'vehicle.bookingSource',
                                        '',
                                    )

                                    formik.setFieldValue('vehicle.invoice', '')
                                }}
                                onTextInputMode={(active) => {
                                    setModelAsTextInput(active)
                                    if (active) {
                                        formik.setFieldTouched(
                                            'vehicle.model',
                                            false,
                                            false,
                                        )

                                        formik.setFieldTouched(
                                            'vehicle.trim',
                                            false,
                                            false,
                                        )
                                    }
                                }}
                            />
                            {modelAsTextInput ? (
                                <FormikTextInput
                                    name="vehicle.trim"
                                    label="Trim"
                                    prefilled={prefilled}
                                />
                            ) : (
                                <TrimDropdown
                                    formik={formik}
                                    prefilled={fromVinFull || prefilled}
                                    onAutomobileSelected={
                                        handleAutomobileSelected
                                    }
                                    onTextInputMode={(active) =>
                                        setTrimAsTextInput(active)
                                    }
                                    availableAutomobileIds={vinAvailableIds}
                                />
                            )}
                        </>
                    )}
                </FormGridPlacement>
                <FormGridPlacement gridArea=" 2 / 2 ">
                    {condition === 'used' && (
                        <>
                            {(modelAsTextInput ||
                                trimAsTextInput ||
                                nadaNoValuation ||
                                bookingSource ===
                                    BookingSource.LIKE_INVOICE) && (
                                <BookingSourceDropdown
                                    year={formik.values.vehicle.year}
                                    prefilled={prefilled}
                                />
                            )}
                            <FormikAmountField
                                name="vehicle.mileage"
                                label="Mileage"
                                placeholder="0"
                                symbol=""
                                decimalScale={0}
                                prefilled={prefilled}
                                onBlurCallback={handleMileageBlur}
                            />
                            {!bSLikeInvoiceCondition &&
                                (modelAsTextInput ||
                                    trimAsTextInput ||
                                    nadaNoValuation) && (
                                    <FormikAmountField
                                        name="vehicle.wholesale"
                                        label="Clean Trade in (including add-ons and mileage adjustments)"
                                        placeholder="0"
                                        decimalScale={0}
                                        prefilled={prefilled}
                                    />
                                )}
                            {bSLikeInvoiceCondition && (
                                <>
                                    <FormikAmountField
                                        name="vehicle.likeInvoice"
                                        label="Like Invoice"
                                        placeholder="0"
                                        decimalScale={0}
                                        prefilled={prefilled}
                                    />
                                    <FormikAmountField
                                        name="vehicle.lendingValue"
                                        label="Lending Value"
                                        placeholder="0"
                                        disabled
                                        tooltip={<LendingValueTooltipText />}
                                        decimalScale={0}
                                        prefilled={prefilled}
                                    />
                                </>
                            )}
                            {isUsedAutomobile && (
                                <CpoCheckboxContainer>
                                    <FormikCheckbox
                                        checked={!!vehicle.isCpo}
                                        error={false}
                                        id="vehicle.isCpo"
                                        label=""
                                        value="vehicle.isCpo"
                                        onChange={(checked) =>
                                            formik.setFieldValue(
                                                'vehicle.isCpo',
                                                checked,
                                            )
                                        }
                                        data-testid="vehicle.isCpo"
                                        prefilled={prefilled}
                                    />
                                    <CpoCheckboxLabel htmlFor="vehicle.isCpo">
                                        This is a Certified Pre-owned Vehicle
                                    </CpoCheckboxLabel>
                                </CpoCheckboxContainer>
                            )}
                        </>
                    )}
                    {condition === 'new' && (
                        <FormikAmountField
                            name="vehicle.invoice"
                            label="Invoice"
                            placeholder="0"
                            decimalScale={0}
                            prefilled={prefilled}
                        />
                    )}
                </FormGridPlacement>
            </FormContent>
            <FormContent gridTemplate="1fr">
                {prefilled &&
                    condition === 'used' &&
                    selectedOptions.length > 0 && (
                        <AccessoriesSection>
                            <AccessoriesTitle>
                                Installed Options + Accessories
                            </AccessoriesTitle>
                            <Grid cols={4}>
                                {groupAccessoriesByCategory(
                                    selectedOptions,
                                ).map(([category, accessories]) => (
                                    <Grid.Item key={category}>
                                        <AccessoryCategoryTitle>
                                            {category}
                                        </AccessoryCategoryTitle>
                                        {accessories.map((accessory) => (
                                            <AccessoryItem
                                                key={accessory.acccode}
                                            >
                                                <FormikCheckbox
                                                    checked
                                                    error={false}
                                                    id={`accessory-${accessory.acccode}`}
                                                    label=""
                                                    value={accessory.acccode}
                                                    onChange={() => {}}
                                                    disabled
                                                    prefilled
                                                />
                                                <span>{accessory.accdesc}</span>
                                            </AccessoryItem>
                                        ))}
                                    </Grid.Item>
                                ))}
                            </Grid>
                        </AccessoriesSection>
                    )}
                {!prefilled &&
                    condition === 'used' &&
                    bookingSource === BookingSource.NADA &&
                    allAccessoriesGrouped.length > 0 && (
                        <AccessoriesSection>
                            <AccessoriesTitle>
                                Installed Options + Accessories
                            </AccessoriesTitle>
                            <Grid cols={4}>
                                {allAccessoriesGrouped.map(
                                    ([category, accessories]) => (
                                        <Grid.Item key={category}>
                                            <AccessoryCategoryTitle>
                                                {category}
                                            </AccessoryCategoryTitle>
                                            {accessories
                                                .filter((a) => a.isincluded)
                                                .map((accessory) => (
                                                    <AccessoryItem
                                                        key={accessory.acccode}
                                                    >
                                                        <FormikCheckbox
                                                            checked
                                                            error={false}
                                                            id={`accessory-${accessory.acccode}`}
                                                            label=""
                                                            value={
                                                                accessory.acccode
                                                            }
                                                            onChange={() => {}}
                                                            disabled
                                                            prefilled={false}
                                                        />
                                                        <span>
                                                            {accessory.accdesc}
                                                        </span>
                                                    </AccessoryItem>
                                                ))}
                                            {accessories
                                                .filter((a) => !a.isincluded)
                                                .map((accessory) => {
                                                    const isBundledChild =
                                                        bundledChildCodes.has(
                                                            accessory.acccode,
                                                        )

                                                    const isExcluded =
                                                        excludedCodes.has(
                                                            accessory.acccode,
                                                        )

                                                    const isChecked =
                                                        selectedOptionCodes.includes(
                                                            accessory.acccode,
                                                        )

                                                    const isDisabled =
                                                        isBundledChild ||
                                                        isExcluded

                                                    return (
                                                        <AccessoryItem
                                                            key={
                                                                accessory.acccode
                                                            }
                                                        >
                                                            <FormikCheckbox
                                                                checked={
                                                                    isChecked
                                                                }
                                                                error={false}
                                                                id={`accessory-${accessory.acccode}`}
                                                                label=""
                                                                value={
                                                                    accessory.acccode
                                                                }
                                                                onChange={(
                                                                    checked,
                                                                ) =>
                                                                    handleAccessoryToggle(
                                                                        accessory,
                                                                        checked,
                                                                    )
                                                                }
                                                                disabled={
                                                                    isDisabled
                                                                }
                                                                prefilled={
                                                                    false
                                                                }
                                                            />
                                                            <span>
                                                                {
                                                                    accessory.accdesc
                                                                }
                                                                {isBundledChild &&
                                                                    ' (included with package)'}
                                                            </span>
                                                        </AccessoryItem>
                                                    )
                                                })}
                                        </Grid.Item>
                                    ),
                                )}
                            </Grid>
                        </AccessoriesSection>
                    )}
            </FormContent>
        </>
    )
}

AutomobileVehicleSelection.defaultProps = defaultProps
